import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild, computed, effect, inject, model, output, signal } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ISeriesEntry } from 'src/app/_models';
import { BookService } from 'src/app/services/book.service';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { debounceTime, map, Observable, startWith } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { AuthService, LogService, MessageService } from 'src/app/services';

/**
 * SeriesInputComponent is a standalone component that allows the user to 
 * select a series name from a list of series names. It also allows the user to
 * enter a reading order for the series. 
 * The component can then Add the new series the current list and emit the result
 */
@Component({
  selector: 'kbm-series-input',
  templateUrl: './series-input.component.html',
  styleUrls: ['./series-input.component.scss'],
  standalone: true,
  imports: [
    MatSelectModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    ReactiveFormsModule,
    AsyncPipe
  ],
})
export class SeriesInputComponent {
  @ViewChild('nameAuto') matAutocomplete!: MatAutocomplete;

  series = model.required<ISeriesEntry[] | null>();
  seriesUpdated = output<ISeriesEntry[]>();
  cancel = output<void>();


  authService = inject(AuthService);
  bookService = inject(BookService);
  logService = inject(LogService);
  messageService = inject(MessageService);
  fb = inject(FormBuilder);

  isAuthenticated = computed(() => {
    return this.authService.isLoggedIn();
  });


  allSeriesNames = signal<string[]>([]);
  form = this.fb.group({
    seriesName: ['', { validators: Validators.required }],
    readingOrder: [0, { validators: Validators.required }],
  });

  filteredSeriesNames!: Observable<string[]>;

  constructor() {
    this.loadSeriesNames();
  }

  ngOnInit(): void {

    this.clearSeriesForm();

    this.filteredSeriesNames = this.form.get('seriesName')!.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        map((value: any) => this._filter(value || '')),
      );
  }

  async loadSeriesNames() {
    try {
      const nameList = await this.bookService.getSeries();
      // pop "-- Not Part of a Series --"  Should be entry [0]
      nameList.splice(0, 1);
      this.allSeriesNames.set(nameList.map(s => s.seriesName))
    } catch (err) {
      console.error(err);
    }
  }

  clearSeriesForm() {
    this.form.setValue({
      seriesName: '',
      readingOrder: 0
    });
  }

  isValid() {
    const name = this.form.get('seriesName')?.value;
    const readingOrder = this.form.get('readingOrder')?.value;
    if (name === '') {
      return false;
    }
    if (typeof (readingOrder) != 'number') {
      return false;
    }
    return true;
  }

  addSeriesEvt(evt: any): void {
    let { seriesName, readingOrder } = this.form.value;
    if (!this.isValid()) {
      this.logService.error('Series name or reading order is empty');
      return;
    }
    if (typeof (readingOrder) != 'number') {
      this.logService.error('Reading order is not a number');
      return;
    }
    if (seriesName) {
      seriesName = seriesName.trim();
      if (this.series() === null) {
        this.series.set([{ "name": seriesName, "readingOrder": readingOrder }]);
      } else {
        if (this.series()!.findIndex((s: any) => s.name === seriesName) !== -1) {
          this.logService.error('Series name already exists');
          this.messageService.showMessage('Series name already exists', 'error');
          return;
        }
        if (this.series()!.length > 0) {
          this.series.set([
            ...this.series()!,
            { "name": seriesName, "readingOrder": readingOrder }]
          );
        }
        else {
          this.series.set([{ "name": seriesName, "readingOrder": readingOrder }]);
        }
        this.clearSeriesForm();
      }
    }
    this.seriesUpdated.emit(this.series()!);
  }


  cancelEvt() {
    this.cancel.emit();
  }


  private _filter(name: string): string[] {
    const filterValue = name.toLowerCase();
    return this.allSeriesNames().filter(
      (entry) => entry.toLowerCase().includes(filterValue)
    );
  }
}
